import React, { useState, useEffect, useRef } from "react";
import ClientList from "./ClientList";
import JsList from "./JsList";
import axios from "axios";
import DateTimePicker from "./DateTimePicker";
import { format } from "date-fns";
import { toast } from "react-toastify";
import ConfirmDialog from "./ConfirmDialog";
import LoadingOverlay from "./LoadingOverlay";
import { TbSearch } from "react-icons/tb";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400";

const NewOrder = () => {
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedInitJobId, setSelectedInitJobId] = useState(null);
  const [jobsheet, setJobsheet] = useState([]);
  const [qtyOrder, setQtyOrder] = useState("");
  const [poOrder, setPoOrder] = useState("");
  const [inputDateSelected, setInputDateSelected] = useState(new Date());
  const [deliveryDateSelected, setDeliveryDateSelected] = useState(
    new Date(new Date().setDate(new Date().getDate() + 7)),
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [jsIdSearch, setJsIdSearch] = useState("");
  const [clientResetKey, setClientResetKey] = useState(0);
  const [clientAxValue, setClientAxValue] = useState(undefined);
  const [clientAxLabel, setClientAxLabel] = useState(undefined);
  const skipClientEffect = useRef(false);

  const handleClientChange = (clientId) => setSelectedClientId(clientId);

  const handleJobsheetSearch = async () => {
    const id = jsIdSearch.trim();
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/jobsheet/search/${id}`,
        { withCredentials: true },
      );
      const js = res.data.result;
      // Fetch jobsheet list first so we have the match ready
      const jsRes = await axios.get(
        `https://eps.elkomprintama.com/get/api/jobsheet/${js.cust_id}`,
      );
      const list = jsRes.data.result || [];
      const match = list.find((j) => j.job_id === js.job_id);
      // Skip the useEffect re-fetch triggered by setSelectedClientId
      skipClientEffect.current = true;
      setClientAxValue(js.cust_id);
      setClientAxLabel(js.cust_name);
      setClientResetKey((k) => k + 1);
      setSelectedClientId(js.cust_id);
      setJobsheet(list);
      if (match) {
        setSelectedJobId(match.job_id);
        setSelectedInitJobId({ value: match.job_id, label: match.nme });
      }
      toast.success(`Jobsheet #${id} found — ${js.job_name}`);
    } catch (err) {
      skipClientEffect.current = false;
      toast.error(err?.response?.data?.msg || "Jobsheet not found.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedClientId) return;
    if (skipClientEffect.current) {
      skipClientEffect.current = false;
      return;
    }
    const fetchJobsheet = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `https://eps.elkomprintama.com/get/api/jobsheet/${selectedClientId}`,
        );
        const result = response.data.result || [];
        setJobsheet(result);
        setSelectedJobId(result[0]?.job_id || null);
        setSelectedInitJobId(
          result.length > 0
            ? { value: result[0].job_id, label: result[0].nme }
            : null,
        );
      } catch {
        toast.error("Error fetching jobsheet data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchJobsheet();
  }, [selectedClientId]);

  const insertOrder = async () => {
    setIsLoading(true);
    try {
      await axios.post("https://eps.elkomprintama.com/post/api/orders/", {
        jobId: selectedJobId,
        custId: selectedClientId,
        orderDate: format(inputDateSelected, "yyyy-MM-dd"),
        orderQty: qtyOrder,
        poNum: poOrder,
        dlvrDate: format(deliveryDateSelected, "yyyy-MM-dd"),
        oprId: 1,
        statusId: 1,
        qcId: 1,
      });
      toast.success("Order created!");
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Failed to insert order.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedClientId || !selectedJobId || !qtyOrder || !poOrder) {
      toast.warning("Please fill in all required fields.");
      return;
    }
    setShowConfirm(true);
    setUploadPending(true);
  };

  const handleDeliveryDateChange = (date) => {
    if (date < inputDateSelected) {
      toast.error("Delivery date cannot be before order date.");
      return;
    }
    setDeliveryDateSelected(date);
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {/* Header */}
      <div className="w-full rounded-lg bg-purple-600 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          New Order
        </label>
      </div>

      {/* Form card */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 max-w-2xl mx-auto">
          {isLoading && <LoadingOverlay message="Loading" />}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Jobsheet ID quick search */}
            <div>
              <label className={lbl}>Search by Jobsheet ID</label>
              <div className="flex gap-2">
                <input
                  className={`${inp} flex-1`}
                  type="number"
                  placeholder="e.g. 1023"
                  value={jsIdSearch}
                  onChange={(e) => setJsIdSearch(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    (e.preventDefault(), handleJobsheetSearch())
                  }
                />
                <button
                  type="button"
                  onClick={handleJobsheetSearch}
                  className="flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 transition flex items-center gap-1"
                >
                  <TbSearch size={15} /> Search
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Enter a jobsheet ID to auto-fill customer and jobsheet below.
              </p>
            </div>

            <hr className="border-gray-100" />

            {/* Customer */}
            <div>
              <ClientList
                key={clientResetKey}
                axValue={clientAxValue}
                axLabel={clientAxLabel}
                onInitial={handleClientChange}
                onChangeList={handleClientChange}
              />
            </div>

            {/* Jobsheet */}
            <div>
              <JsList
                onChangeList={(opt) => {
                  setSelectedJobId(opt.value);
                  setSelectedInitJobId(opt);
                }}
                jobsheet={jobsheet}
                initialValue={selectedInitJobId}
              />
            </div>

            {/* Dates */}
            <div>
              <DateTimePicker
                firstDateValue={inputDateSelected}
                onChangeFirstDate={setInputDateSelected}
                secondDateValue={deliveryDateSelected}
                onChangeSecondDate={handleDeliveryDateChange}
                firstLabel="Order Date"
                secondLabel="Delivery Date"
              />
            </div>

            {/* Qty + PO */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Qty</label>
                <input
                  className={inp}
                  name="qtyorder"
                  type="text"
                  placeholder="e.g. 1000"
                  value={qtyOrder}
                  onChange={(e) => setQtyOrder(e.target.value.toUpperCase())}
                />
              </div>
              <div>
                <label className={lbl}>PO Number</label>
                <input
                  className={inp}
                  name="poorder"
                  type="text"
                  placeholder="e.g. PO-12345"
                  value={poOrder}
                  onChange={(e) => setPoOrder(e.target.value.toUpperCase())}
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="w-full py-3 rounded-xl text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 transition"
            >
              Create Order
            </button>
          </form>
        </div>
      </div>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Are you sure you want to create this order?"
          onConfirm={() => {
            setShowConfirm(false);
            setUploadPending(false);
            insertOrder();
            setJobsheet([]);
            setQtyOrder("");
            setPoOrder("");
            setInputDateSelected(new Date());
            setDeliveryDateSelected(
              new Date(new Date().setDate(new Date().getDate() + 7)),
            );
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
    </div>
  );
};

export default NewOrder;
